import SectionHeading from "./SectionHeading";

const chapters = [
  {
    id: "read-a",
    no: "壹",
    title: "第一步：先找到信息源头，而不是标题",
    body: "考古的第一步不是急着看结论，而是回到事件最初的信息源头。同一个话题在不同平台会呈现完全不同的切面，只有把原始表达找出来，才能判断后续的转发到底在原意上叠加了什么。",
  },
  {
    id: "read-b",
    no: "贰",
    title: "第二步：给每个关键节点建立时间戳",
    body: "把事件拆成一个个可以核对的时间点，是看清脉络最有效的方式。什么时候被曝光、什么时候开始发酵、又是什么时候出现第一次澄清，把这些节点连成一条线，很多原本混乱的讨论会立刻变得清晰。",
  },
  {
    id: "read-c",
    no: "叁",
    title: "第三步：区分事实、判断与情绪",
    body: "围观久了你会发现，真正决定一个话题走向的，往往不只是事实本身，还包括当时大众的情绪状态。我们在档案中会尽量把'公开可见的信息'与'个人判断'区分开，前者交给读者自行核对，后者作为编辑视角单独标注。",
  },
  {
    id: "read-d",
    no: "肆",
    title: "第四步：让热度退去后，仍有资料可查",
    body: "很多热议当时轰轰烈烈，热度过去后却只留下零散截图。档案的价值在于，当讨论冷却下来，仍能有一份相对完整、按时间整理好的资料，让后来的人不必从零开始拼图。这也是我们把旧料归档整理的初衷。",
  },
];

const sidePoints = [
  "按公开信息与时间线整理，不追逐未经证实的下文",
  "把事实、编辑判断与情绪单独分层标注",
  "坚持交叉求证，避免只依赖单一来源的表述",
  "热度退去后依然保留完整档案，方便后来者查阅",
];

export default function DeepRead() {
  return (
    <section id="deep" className="bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="overflow-hidden rounded-xl border border-background-200">
          {/* 头图 */}
          <div className="relative aspect-[21/9] md:aspect-[21/7]">
            <img
              src="https://readdy.ai/api/search-image?query=old%20archive%20desk%20with%20manila%20folders%2C%20magnifying%20glass%20and%20timeline%20string%20on%20warm%20cream%20paper%2C%20vintage%20editorial%20photography%2C%20investigative%20reading%20mood%2C%20deep%20terracotta%20and%20amber%20tones%2C%20dramatic%20soft%20light%2C%20no%20readable%20text%2C%20high%20detail&width=1800&height=760&seq=hg-deep&orientation=landscape"
              alt="深度回顾：把一段旧闻读厚的方法论配图"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/70 to-transparent" />
            <div className="absolute bottom-0 left-0 w-full p-5 md:p-8">
              <div className="flex flex-wrap items-center gap-2 text-xs text-background-200">
                <span className="rounded-full bg-accent-500 px-2.5 py-1 font-semibold text-primary-950">
                  深度回顾
                </span>
                <span>约 6 分钟读完 · 归档方法专题</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-8 p-5 md:p-8 lg:grid-cols-3">
            {/* 正文 */}
            <article className="lg:col-span-2">
              <h3 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                把一段旧闻读厚：给考古新手的四步方法
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                很多人问，翻旧瓜到底在翻什么？其实高质量的考古，不只是找回一份热闹，更是练习一种把散落信息重新组织起来的能力。下面这份方法，是我们在整理往期时一直在用的路线。
              </p>

              <div className="mt-7 flex flex-col gap-6">
                {chapters.map((c) => (
                  <section key={c.id} id={c.id} className="scroll-mt-24 border-l-2 border-background-200 pl-5">
                    <h4 className="font-heading text-lg font-bold text-foreground-950">
                      <span className="mr-2 text-primary-600">{c.no}</span>
                      {c.title}
                    </h4>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{c.body}</p>
                  </section>
                ))}
              </div>
            </article>

            {/* 侧栏 */}
            <aside className="lg:sticky lg:top-24 lg:self-start">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <p className="font-heading text-base font-bold text-foreground-950">本期要点</p>
                <ul className="mt-4 flex flex-col gap-3">
                  {sidePoints.map((p) => (
                    <li key={p} className="flex items-start gap-2.5 text-sm text-foreground-700">
                      <i className="ri-checkbox-circle-line mt-0.5 shrink-0 text-accent-500" />
                      {p}
                    </li>
                  ))}
                </ul>
                <a
                  href="#timeline"
                  className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700"
                >
                  回看完整年份时间线
                  <i className="ri-arrow-right-line" />
                </a>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </section>
  );
}